import * as React from 'react';
import {Helmet} from 'react-helmet';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter, RouterHistory} from 'react-router-dom';

import {useMutation} from '@apollo/react-hooks';

import type {FilterObject} from 'nutshell-core/types';

import {headTitle} from 'shells/document/head-title';
import {ErrorState} from 'shells/error-state/error-state';

import type {
    TemplateFragment as Template,
    TemplateStubFragment as TemplateStub,
    EditionTestEmailSend,
    EditionTestEmailSendVariables,
    EditionUpdateInput,
} from 'nutshell-graphql-types';

import type {ReportURLParams} from '../../reports/report-types';

import {EditionPage} from '../editions/edition-page';
import EDITION_TEST_EMAIL_SEND from '../editions/graphql/mutations/edition-test-email-send.graphql';
import {SavingDebounce} from '../editions/utils/saving-debounce';
import {TemplateQuery} from '../graphql-wrappers/template/template-query';
import {TemplateMutations} from '../templates/template-mutations';

type OwnProps = ContextRouter;

type Props = OwnProps & {
    history: RouterHistory;
    template: Template | null | undefined;
    templateStub: TemplateStub | null | undefined;
    isLoading: boolean;
    onSendEmail: (toEmailAddress: string[]) => Promise<any>;
    onTemplateSaveDebounced: (editionId: string, beeBodyJson: string) => Promise<any>;
    onTemplateSave: (saveData: EditionUpdateInput) => Promise<any>;
    isSaving: boolean;
    savingError?: string;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
    error: string | null | undefined;
};

export function TemplatePageWrapperComponent(props: Props) {
    const setSelectedEditorTab = (newTab: string) => {
        const newPath = generatePath(props.match.path, {
            templateId: props.match.params.templateId,
            editionId: props.match.params.editionId,
            tab: newTab,
        });

        // Make sure to pass the search here, because without it we force a
        // rerender, which causes BEE to reload.
        props.history.replace({
            pathname: newPath,
            search: props.location.search,
        });
    };

    const pageTitle = props.template ? `${props.template.name}` : 'Template';

    if (props.error) {
        return <ErrorState errorMessage={props.error} />;
    }

    return (
        <>
            <Helmet>
                <title>{headTitle(pageTitle)}</title>
            </Helmet>
            <EditionPage
                {...props}
                pageTitle={headTitle(pageTitle)}
                onChange={props.onTemplateSaveDebounced}
                onEditionSave={props.onTemplateSave}
                edition={props.template ? props.template.edition : null}
                editionStub={props.templateStub ? props.templateStub.edition : null}
                onSetSelectedEditorTab={setSelectedEditorTab}
                campaignType='TEMPLATE'
                generatePath={(newTab: string) => {
                    const path = generatePath(props.match.path, {
                        templateId: props.match.params.templateId,
                        editionId: props.match.params.editionId,
                        tab: newTab,
                    });

                    return props.location.search && props.location.search.length
                        ? `${path}${props.location.search}`
                        : path;
                }}
            />
        </>
    );
}

export function TemplatePageWrapper(props: OwnProps) {
    const templateIdParam = props.match.params.templateId;
    const templateApiId = templateIdParam ? templateIdParam : '';
    const [sendTestEmail] = useMutation<EditionTestEmailSend, EditionTestEmailSendVariables>(
        EDITION_TEST_EMAIL_SEND
    );

    return (
        <TemplateQuery templateId={templateApiId}>
            {({template, templateStub, isLoading, error}) => (
                <TemplateMutations>
                    {({onUpdate}) => (
                        <SavingDebounce
                            onSave={(saveData: EditionUpdateInput) => {
                                return onUpdate(saveData, templateApiId);
                            }}
                        >
                            {({onChange, isSaving, hasChanged}) => (
                                <TemplatePageWrapperComponent
                                    {...props}
                                    template={template}
                                    templateStub={templateStub}
                                    isLoading={isLoading}
                                    // The behavior in this case is the same if the content has
                                    // changed or if we're saving
                                    isSaving={isSaving || hasChanged}
                                    // @ts-expect-error
                                    onSendEmail={(emailAddresses) => {
                                        const editionId = template && template.edition.id;
                                        if (editionId) {
                                            return sendTestEmail({
                                                variables: {
                                                    input: {
                                                        editionId: editionId,
                                                        emailAddresses: emailAddresses,
                                                    },
                                                },
                                            });
                                        }
                                    }}
                                    onTemplateSaveDebounced={(
                                        editionId: string,
                                        beeBodyJson: string
                                    ) => {
                                        onChange({editionId, beeBodyJson});
                                    }}
                                    onTemplateSave={(saveData: EditionUpdateInput) =>
                                        onUpdate(saveData, templateApiId)
                                    }
                                    error={error}
                                />
                            )}
                        </SavingDebounce>
                    )}
                </TemplateMutations>
            )}
        </TemplateQuery>
    );
}
